import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, Plane, Receipt } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BrandMark({ brand, size = 44 }) {
  if (brand?.logo_data_url) {
    return <img src={brand.logo_data_url} alt="" style={{ width: size, height: size }} className="rounded-xl object-contain bg-white" />;
  }
  return <div className="brand-mark" style={{ width: size, height: size, fontSize: size * 0.5 }}>G</div>;
}

const rupiah = (v) => "Rp " + Number(v || 0).toLocaleString("id-ID");

export default function PublicInvoice() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/invoice/${id}`, { params: { t: token } })
      .then((r) => setData(r.data))
      .catch(() => setErr("Invoice tidak ditemukan atau link tidak valid."));
  }, [id, token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6 brand-hero">
      <div className="paper p-8 text-center">
        <div className="text-red-600 font-semibold mb-2">Tidak dapat menampilkan invoice</div>
        <div className="text-slate-500 text-sm">{err}</div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Memuat...</div>;

  const { invoice, etickets, brand } = data;

  const pdfUrlFor = (eid) => `${API}/public/eticket/${eid}/pdf?inv=${id}&t=${token}`;
  const receiptPdfUrl = `${API}/public/receipt/${id}/pdf?t=${token}`;

  return (
    <div className="min-h-screen brand-hero py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="flex items-center gap-4">
          <BrandMark brand={brand} size={56} />
          <div>
            <div className="font-extrabold text-2xl text-slate-900">{brand.company_name}</div>
            <div className="text-sm text-slate-500">{brand.tagline}</div>
          </div>
        </header>

        <div className="paper p-8" data-testid="public-invoice">
          <div className="flex items-start justify-between border-b pb-4 mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#6b8e23] font-bold">Invoice</div>
              <div className="text-2xl font-extrabold mono mt-1">{invoice.invoice_number}</div>
              <div className="text-xs text-slate-500 mt-1">Tanggal: {invoice.invoice_date}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Total</div>
              <div className="text-2xl font-extrabold text-[#556b2f] mono">{rupiah(invoice.total)}</div>
              <div className="mt-1">
                <span className={`pill ${invoice.payment_status === "Paid" ? "pill-paid" : "pill-unpaid"}`}>{invoice.payment_status}</span>
              </div>
            </div>
          </div>

          <div className="text-sm">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Ditagihkan kepada</div>
            <div className="font-semibold text-slate-900 text-base">{invoice.customer_name}</div>
            {invoice.passenger && <div className="text-slate-600 mt-1">Penumpang: {invoice.passenger}</div>}
            {invoice.pnr && <div className="text-slate-600">PNR: <span className="mono">{invoice.pnr}</span></div>}
            {invoice.airline && <div className="text-slate-600">Maskapai: {invoice.airline}</div>}
            {invoice.route && <div className="text-slate-600">Rute: {invoice.route}</div>}
          </div>

          {invoice.payment_status === "Paid" && (
            <div className="mt-6 pt-4 border-t border-[#e4ecc9]" data-testid="public-receipt-block">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#556b2f] font-bold flex items-center gap-1">
                    <Receipt size={12} /> Bukti Pembayaran
                  </div>
                  <div className="text-sm text-slate-600 mt-1">Pembayaran telah kami terima dengan LUNAS. Klik untuk mengunduh bukti pembayaran resmi.</div>
                </div>
                <a href={receiptPdfUrl} target="_blank" rel="noreferrer">
                  <button className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-lg bg-[#556b2f] text-white hover:bg-[#3d4d1e]" data-testid="public-receipt-download">
                    <Download size={13}/> Unduh Bukti
                  </button>
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="paper p-8" data-testid="public-etickets">
          <div className="flex items-center gap-2 mb-4">
            <Plane size={18} className="text-[#6b8e23]"/>
            <h2 className="font-extrabold text-slate-900">E-ticket ({etickets.length})</h2>
          </div>

          {etickets.length === 0 && (
            <p className="text-sm text-slate-400">Belum ada e-ticket terlampir pada invoice ini.</p>
          )}

          <div className="space-y-4">
            {etickets.map((e) => {
              const names = e.passengers?.map((p) => p.name).filter(Boolean).join(", ") || "-";
              return (
                <div key={e.id} className="border border-slate-200 rounded-xl p-4 hover:border-[#556b2f]/40 transition-colors" data-testid={`public-et-${e.eticket_number}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">{names}</div>
                      <div className="text-xs text-slate-500 mt-0.5 mono">{e.eticket_number} · PNR {e.airline_ref || "-"}</div>
                    </div>
                    <a href={pdfUrlFor(e.id)} target="_blank" rel="noreferrer">
                      <button className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-[#556b2f] text-white hover:bg-[#3d4d1e]" data-testid={`public-et-download-${e.eticket_number}`}>
                        <Download size={13}/> Unduh PDF
                      </button>
                    </a>
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    {(e.flights || []).map((fl, i) => (
                      <div key={i} className="text-slate-700 flex items-center gap-2">
                        <span className="font-semibold mono text-slate-900">{fl.flight_number}</span>
                        <span className="text-slate-500">{fl.carrier}</span>
                        <span className="text-slate-400">·</span>
                        <span>{fl.from_airport} → {fl.to_airport}</span>
                        {fl.depart && <span className="text-slate-400">· {fl.depart}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <footer className="text-center text-xs text-slate-400 pt-2">
          {brand.company_name} · WhatsApp {brand.whatsapp} · {brand.email}
        </footer>
      </div>
    </div>
  );
}
